import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useOrderState } from '@hooks/useOrderState';
import { Header } from '@components/ModalHeader';
import { ModalContent } from '@components/ModalContent';
import { ModalButton } from '@components/ModalButton';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { useSegment } from '@hooks/useSegment';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  MekiCredits,
  InsuranceOptions,
  PaymentSwitch,
  PaymentsOptions,
} from './components';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { PaymentFooter } from './components/PaymentFooter';
import { getRoute, Console } from '@utils';
import {
  InfoDiv,
  InfoIconButton,
  InfoTypography,
} from './styles/InsuranceInfoDiv.styles';
import {
  GetCheckoutPaymentMethodQueryResponse,
  GetCheckoutPaymentMethodQueryParams,
  getCheckoutPaymentMethod,
} from '@queries/checkout/queries/paymentMethod';
import {
  GetUserInsurancesParams,
  GetUserInsurancesResponse,
  getUserInsurances,
} from '@queries/userInsurance/queries';
import {
  GetInsuranceQuoteParams,
  GetInsuranceQuoteResponse,
  getInsuranceQuote,
} from '@queries/userInsurance/queries/getInsuranceQuote';
import {
  GetCheckoutDetailQueryResponse,
  GetCheckoutDetailQueryParams,
  OrderDeliveryOptions,
} from '@queries/checkout/queries/detail';
import { PaymentSwitchContainer } from './styles/PaymentSwitchContainer';
import {
  AssignPaymentMethodMutationParams,
  AssignPaymentMethodMutationResponse,
  assignPaymentMethod,
} from '@queries/checkout/mutations/assignPaymentMethod';
import {
  SetCheckoutOrderDataParams,
  SetCheckoutOrderDataResponse,
  setCheckoutOrderData,
} from '@queries/order/mutations/setCheckoutOrderData';
import { calculateHeight } from './utils';
import {
  usePaymentSelectors,
  usePaymentMethodActions,
} from './zustand/paymentMethodStore';
import { useLocalPaymentOption } from './zustand/localPaymentOptionsStore';
import {
  useAssistantPaymentActions,
  useAssistantPaymentOptions,
} from './zustand/assistantPaymentOptionsStore';
import { Loader } from '@components/Loader';
import { ContentContainer } from '@components/ContentContainer';
import { usePendingPrescriptions } from '@hooks/usePendingPrescriptions';
import { useModalRouter } from '@hooks/useModalRouter';
import { isSafari } from '@config/environment';
import { useMekiState } from '@hooks/useMekiState';
import { isMobile } from 'react-device-detect';

export function CheckoutPaymentMethodV2() {
  const { appUser: user } = useAuth();
  const { userCan } = usePermissions();
  const modalRouter = useModalRouter();
  const { track } = useSegment();

  const {
    refetch: refetchOrder,
    orderUuid,
    orderStatus,
    order: currentOrder,
  } = useOrderState();

  const isLocalVendor = userCan('LOCAL_VENDOR');
  const isAssistantUser = userCan('MANAGE_USER');
  const [payAmount, setPayAmount] = useState<number>(0);

  // Zustand
  const { mekiCreditsChecked, totalReimbursement, totalDeductible } =
    usePaymentSelectors();

  const { insuranceId, setInsuranceId, setInsuranceRut } = useMekiState();

  const {
    setOrders,
    setOrdersTotal,
    setPaymentMethods,
    setInsuranceTotalInfo,
    setMekiCreditsChecked,
  } = usePaymentMethodActions();

  const { sendEmail, chargeDeliveryCost } = useAssistantPaymentOptions();
  const { setChargeDeliveryCost } = useAssistantPaymentActions();
  const { paymentOption } = useLocalPaymentOption();
  const [redirecting, setRedirecting] = useState<boolean>(false);

  const [quoteLoading, setQuoteLoading] = useState<boolean>(false);

  // Queries
  const { data: orderAndUserInfoData, loading: loadingMethod } = useQuery<
    GetCheckoutPaymentMethodQueryResponse,
    GetCheckoutPaymentMethodQueryParams
  >(getCheckoutPaymentMethod(), {
    variables: { params: { orderUuid } },
    fetchPolicy: 'cache-first',
  });

  const {
    data: { orderDeliveryOptions } = {},
    loading: loadingDeliveryOptions,
  } = useQuery<GetCheckoutDetailQueryResponse, GetCheckoutDetailQueryParams>(
    OrderDeliveryOptions(),
    {
      variables: { params: { orderUuid } },
      fetchPolicy: 'cache-and-network',
    },
  );

  const { currentUserInfo, paymentMethods } = orderAndUserInfoData ?? {};

  const { data: insurancesData, loading: loadingInsurances } = useQuery<
    GetUserInsurancesResponse,
    GetUserInsurancesParams
  >(getUserInsurances(), {
    fetchPolicy: 'no-cache',
    variables: { params: { userId: user?.id } },
  });

  const { requirePrescriptions } = usePendingPrescriptions();

  const [setCheckoutData] = useMutation<
    SetCheckoutOrderDataResponse,
    SetCheckoutOrderDataParams
  >(setCheckoutOrderData());

  const [
    getInsuranceQuoteLazy,
    {
      data: quoteData,
      loading: loadingGetInsuranceQuote,
      error: insuranceQuoteError,
    },
  ] = useLazyQuery<GetInsuranceQuoteResponse, GetInsuranceQuoteParams>(
    getInsuranceQuote(),
    {
      fetchPolicy: 'no-cache',
    },
  );

  const loadInsuranceQuote = useCallback(
    async (rut: string) => {
      if (orderStatus != 'DRAFT') {
        Console.log('loadInsuranceQuote not ran. Order status is not DRAFT');
        return;
      }
      try {
        setQuoteLoading(true);
        const quoteDataResponse = await getInsuranceQuoteLazy({
          variables: {
            params: {
              rut,
            },
          },
        });
      } catch (error) {
        Console.log('Loading insurance quote failed', error);
        throw error;
      } finally {
        setQuoteLoading(false);
      }
    },
    [getInsuranceQuoteLazy, orderStatus],
  );

  useEffect(() => {
    const fetchData = async () => {
      if (insurancesData?.getUserInsurances?.length !== 0) {
        const mostRecentInsurance = insurancesData?.getUserInsurances?.reduce(
          (latest, current) => {
            return new Date(current.usedAt) > new Date(latest.usedAt)
              ? current
              : latest;
          },
        );
        if (mostRecentInsurance) {
          try {
            setInsuranceId(mostRecentInsurance.id);
            setInsuranceRut(mostRecentInsurance.rut);
            await loadInsuranceQuote(mostRecentInsurance?.rut);
          } catch (error) {
            setInsuranceId(null);
            setInsuranceRut(null);
            Console.log('Loading insurance quote failed', error);
          }
        }
      }
    };
    void fetchData();
  }, [
    insurancesData?.getUserInsurances,
    setInsuranceId,
    setInsuranceRut,
    loadInsuranceQuote,
  ]);

  const userHasInsurances = useMemo(() => {
    return (
      Boolean(user?.hasUserInsurance) &&
      insurancesData?.getUserInsurances?.length > 0
    );
  }, [user?.hasUserInsurance, insurancesData?.getUserInsurances?.length]);

  const orders = orderDeliveryOptions?.find(
    (item) => item.dates.length === (currentOrder?.orderGroupId ? 2 : 1),
  )?.orders;

  const orderDetailsAmount = orders?.reduce((acc, order) => {
    return acc + order.orderDetails.length;
  }, 0);

  const ordersTotal = useMemo(() => {
    return (
      orders?.reduce((acc, order) => {
        const orderTotal = order.orderDetails.reduce((acc, detail) => {
          return acc + detail.quantity * detail.price;
        }, 0);

        if (!chargeDeliveryCost && isAssistantUser) {
          return acc + orderTotal;
        }

        return acc + orderTotal + (chargeDeliveryCost ? order.deliveryCost : 0);
      }, 0) ?? 0
    );
  }, [chargeDeliveryCost, isAssistantUser, orders]);

  const contentLoading =
    loadingDeliveryOptions || loadingMethod || !orderDeliveryOptions;

  const showInsuranceOptions =
    !mekiCreditsChecked || currentUserInfo?.credits == 0;

  const [assignMethod] = useMutation<
    AssignPaymentMethodMutationResponse,
    AssignPaymentMethodMutationParams
  >(assignPaymentMethod());

  useEffect(() => {
    if (currentUserInfo?.credits === 0 || userHasInsurances) {
      setMekiCreditsChecked(false);
    }
  }, [currentUserInfo?.credits, setMekiCreditsChecked, userHasInsurances]);

  const reimbursedProductIds =
    quoteData?.getInsuranceQuote?.reimbursedProducts?.map(
      (product) => +product.sku,
    );
  const productsWithInsurance = reimbursedProductIds?.length ?? 0;
  useEffect(() => {
    const finalReimbursement =
      (totalReimbursement ?? 0) - (totalDeductible ?? 0);

    const ordersTotalFinal = mekiCreditsChecked
      ? Math.max(ordersTotal - currentUserInfo?.credits, 0)
      : insuranceId
      ? ordersTotal - finalReimbursement
      : ordersTotal;
    setPayAmount(ordersTotalFinal);
  }, [
    mekiCreditsChecked,
    ordersTotal,
    quoteData,
    currentUserInfo?.credits,
    currentUserInfo,
    totalReimbursement,
    insuranceId,
    totalDeductible,
  ]);

  const showNextButton =
    (mekiCreditsChecked && payAmount == 0) ||
    (isAssistantUser && paymentOption == 'TRANSFER') ||
    (!mekiCreditsChecked && payAmount == 0);

  const assignOrderMetadata = useCallback(async () => {
    const {
      claimId,
      insuranceId: userInsuranceId,
      totalReimbursement,
      totalDeductible,
      totalCopay,
      reimbursedProducts,
    } = quoteData?.getInsuranceQuote ?? {};
    const cleanReimbursedProducts = reimbursedProducts?.map((product) => {
      if (product.coverage) {
        return {
          sku: +product.sku,
          observation: product.observation,
          unitReimbursement: product.unitReimbursement,
          unitDeductible: product.unitDeductible,
          unitCopay: product.unitCopay,
          quantity: product.quantity,
          coverage: product.coverage,
        };
      }
    });

    await setCheckoutData({
      variables: {
        params: {
          orderId: currentOrder?.id,
          replace: false,
          metadata: {
            useMekiCredits: mekiCreditsChecked ?? false,
            chargeDeliveryCost: chargeDeliveryCost ?? true,
            insuranceInfo: !insuranceId
              ? null
              : {
                  claimId: +claimId ?? null,
                  rut: quoteData?.getInsuranceQuote?.metadata?.rut ?? '',
                  insuranceId: userInsuranceId ?? null,
                  totalReimbursement: totalReimbursement ?? 0,
                  totalDeductible: totalDeductible ?? 0,
                  totalCopay: totalCopay ?? 0,
                  reimbursedProducts: cleanReimbursedProducts ?? [],
                },
            localVendor: null,
            assistantVendor: isAssistantUser
              ? {
                  paymentMethod: paymentOption as 'TRANSFER' | 'CARD',
                }
              : null,
            sendEmail: sendEmail ?? true,
            userInfo: null,
            installments: 0,
          },
        },
      },
    });
    await refetchOrder();
  }, [
    quoteData?.getInsuranceQuote,
    setCheckoutData,
    currentOrder?.id,
    mekiCreditsChecked,
    chargeDeliveryCost,
    insuranceId,
    isAssistantUser,
    paymentOption,
    sendEmail,
    refetchOrder,
  ]);

  const handleNext = useCallback(
    async (intallmentsRedirect?: boolean) => {
      setRedirecting(true);
      await assignOrderMetadata();
      if (intallmentsRedirect && payAmount > 0) {
        void modalRouter.push(getRoute('checkoutInstallments'));
        return;
      }
      if (requirePrescriptions) {
        void modalRouter.push(getRoute('checkoutPrescriptions'));
        return;
      }
      void modalRouter.push(getRoute('checkoutPaymentConfirm'));
    },
    [assignOrderMetadata, modalRouter, payAmount, requirePrescriptions],
  );

  useEffect(
    function savePaymentMethodsValue() {
      setPaymentMethods(paymentMethods);
    },
    [paymentMethods, setPaymentMethods],
  );

  useEffect(
    function setZustandInitialValues() {
      setChargeDeliveryCost(
        isLocalVendor || currentOrder?.deliveryCost == 0 ? false : true,
      );
    },
    [currentOrder?.deliveryCost, isLocalVendor, setChargeDeliveryCost],
  );

  useEffect(
    function refreshOrderTotalPayAmount() {
      setOrders(orders);
      setOrdersTotal(ordersTotal);
    },
    [orders, ordersTotal, setOrders, setOrdersTotal],
  );

  useEffect(
    function refreshTotalReimbursement() {
      setInsuranceTotalInfo({
        totalReimbursement:
          quoteData?.getInsuranceQuote?.totalReimbursement ?? 0,
        totalDeductible: quoteData?.getInsuranceQuote?.totalDeductible ?? 0,
      });
    },

    [quoteData?.getInsuranceQuote, setInsuranceTotalInfo],
  );

  useEffect(() => {
    if (!mekiCreditsChecked && payAmount === 0 && !contentLoading) {
      const assingMekiCreditPaymentMethod = async () => {
        await assignMethod({
          variables: {
            params: {
              orderUuid,
              paymentMethodUuid: '00000000-0000-0000-0000-00000006',
            },
          },
          optimisticResponse() {
            return {
              assignPaymentMethod: {
                __typename: 'Order',
                id: currentOrder?.id,
                uuid: orderUuid,
                paymentMethod: {
                  id: -7,
                },
              },
            };
          },
        });
      };
      void assingMekiCreditPaymentMethod();
      void track('add payment succeed', {
        payment_method_type: 'INSURANCE',
        userId: user?.id,
      });
    }
    if (mekiCreditsChecked && payAmount === 0) {
      const assingMekiCreditPaymentMethod = async () => {
        await assignMethod({
          variables: {
            params: {
              orderUuid,
              paymentMethodUuid: '00000000-0000-0000-0000-00000000',
            },
          },
          optimisticResponse() {
            return {
              assignPaymentMethod: {
                __typename: 'Order',
                id: currentOrder?.id,
                uuid: orderUuid,
                paymentMethod: {
                  id: -1,
                },
              },
            };
          },
        });
      };
      void assingMekiCreditPaymentMethod();
      void track('add payment succeed', {
        payment_method_type: 'MEKI_CREDIT',
        userId: user?.id,
      });
    }
  }, [
    mekiCreditsChecked,
    assignMethod,
    currentOrder?.id,
    orderUuid,
    track,
    payAmount,
    user?.id,
  ]);

  return (
    <>
      <ContentContainer background="#F5F5F5" bottomNavigation={isMobile}>
        <Header
          title={'Método de pago'}
          backIcon
          backFunction={() => {
            void modalRouter.push(getRoute('checkoutDetail'));
          }}
        />
        <ModalContent
          isSafari={isSafari}
          desktopHeight={calculateHeight(
            'desktop',
            showNextButton,
            userHasInsurances,
          )}
          mobileHeight={calculateHeight(
            'mobile',
            showNextButton,
            userHasInsurances,
          )}
        >
          {contentLoading || redirecting ? (
            <Loader />
          ) : (
            <Fragment>
              {isAssistantUser && (
                <PaymentSwitchContainer>
                  <PaymentSwitch
                    label="Enviar email"
                    loading={contentLoading}
                  />
                  <PaymentSwitch
                    label="Incluir costo de despacho"
                    loading={contentLoading}
                  />
                </PaymentSwitchContainer>
              )}
              {currentUserInfo?.credits > 0 && (
                <>
                  <MekiCredits loading={contentLoading} />
                  {insurancesData?.getUserInsurances?.length > 0 && (
                    <InfoDiv hidden={contentLoading || !mekiCreditsChecked}>
                      <InfoIconButton>
                        <InfoOutlinedIcon />
                      </InfoIconButton>
                      <InfoTypography>
                        Los Créditos Meki no pueden utilizarse junto a un
                        seguro.
                      </InfoTypography>
                    </InfoDiv>
                  )}
                </>
              )}
              {insurancesData?.getUserInsurances?.length > 0 && (
                <InsuranceOptions
                  loadingInsurances={loadingInsurances}
                  loadingCircle={loadingGetInsuranceQuote}
                  loadInsuranceQuote={loadInsuranceQuote}
                  mekiCreditsChecked={!showInsuranceOptions}
                  userInsurances={insurancesData?.getUserInsurances}
                  quoteError={insuranceQuoteError}
                />
              )}

              <InfoDiv
                hidden={
                  !insuranceId ||
                  (insuranceId && quoteLoading) ||
                  !userHasInsurances ||
                  !!insuranceQuoteError
                }
              >
                <InfoIconButton>
                  <InfoOutlinedIcon />
                </InfoIconButton>
                {productsWithInsurance == 0 ? (
                  <InfoTypography>
                    Tu seguro no cubre ninguno de los productos agregados al
                    carro.
                  </InfoTypography>
                ) : (
                  <InfoTypography>
                    {orderDetailsAmount == productsWithInsurance
                      ? 'Todos tus productos tienen cobertura.'
                      : productsWithInsurance == 1
                      ? `
                      ${productsWithInsurance} de tus productos tiene cobertura.`
                      : `${productsWithInsurance} de tus productos tienen cobertura.`}{' '}
                    {payAmount != 0
                      ? 'Selecciona un método de pago para ver más detalles.'
                      : ''}
                  </InfoTypography>
                )}
              </InfoDiv>

              {(quoteLoading || payAmount > 0) && (
                <PaymentsOptions
                  loading={contentLoading}
                  quoteLoading={quoteLoading}
                  handleNext={handleNext}
                />
              )}
            </Fragment>
          )}
        </ModalContent>
        {showNextButton ? (
          <ModalButton handleNext={() => handleNext()} text={'Siguiente'}>
            {!isAssistantUser && (
              <PaymentFooter
                payAmount={payAmount}
                userHasInsurances={userHasInsurances}
                quoteLoading={quoteLoading}
                showNextButton={showNextButton}
              />
            )}
          </ModalButton>
        ) : (
          <>
            {!isAssistantUser && (
              <PaymentFooter
                payAmount={payAmount}
                userHasInsurances={userHasInsurances}
                quoteLoading={quoteLoading}
                showNextButton={showNextButton}
              />
            )}
          </>
        )}
      </ContentContainer>
    </>
  );
}
